import { UseQueryResult } from '@tanstack/react-query';
import { css } from 'aphrodite';
import { createContext, useContext, useEffect, useState } from 'react';
import { IAccountTag } from '../../../extViewmodels';
import { useTagSearchQuery } from '../../../queries/Tag';
import { Combobox, ComboboxItem, ComboboxItemRadioIndicator, ComboboxStatus } from '../../components-v2/Combobox';
import { bs } from '../../styles/styles';
import { LoadingSpinner } from '../LoadingSpinner';

interface Props extends React.ComponentProps<typeof Combobox> {
	tagName?: string;
}

const ComboboxSearchInputState = createContext<string>('');

function _TagComboboxStatus({
	tagSearchQuery,
	...props
}: { tagSearchQuery: UseQueryResult<IAccountTag[]> } & React.ComponentProps<typeof ComboboxStatus>) {
	const searchInputValue = useContext(ComboboxSearchInputState);
	let status: React.ReactNode = '';
	if (tagSearchQuery.isLoading) {
		status = (
			<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
				<LoadingSpinner type='extra-tiny' />
				Searching Tags...
			</div>
		);
	} else if (tagSearchQuery.error) {
		status = tagSearchQuery.error.message;
	} else if (tagSearchQuery.data?.length === 0 && searchInputValue) {
		status = `No Tags found for "${searchInputValue}"`;
	} else if (tagSearchQuery.data?.length > 0) {
		status = `${tagSearchQuery.data.length} Tag${tagSearchQuery.data.length === 1 ? '' : 's'} found`;
	} else if (!searchInputValue) {
		status = 'Start typing to search Tags...';
	}
	return <ComboboxStatus {...props}>{status}</ComboboxStatus>;
}

export function TagCombobox(props: Props) {
	const { tagName, ...rest } = props;
	const [searchValue, setSearchValue] = useState('');
	const tagSearchQuery = useTagSearchQuery({
		query: searchValue,
		enabled: Boolean(searchValue),
	});
	useEffect(() => {
		if (!searchValue && tagName) {
			setSearchValue(tagName);
		}
	}, [searchValue, tagName]);
	return (
		<ComboboxSearchInputState.Provider value={searchValue}>
			<Combobox
				items={tagSearchQuery.data ?? []}
				value={tagName ? { id: '', name: tagName } : null}
				inputValue={searchValue}
				onInputValueChange={setSearchValue}
				filter={null}
				itemToStringLabel={item => (item as IAccountTag)?.name ?? ''}
				isItemEqualToValue={(item, value) => {
					return (item as IAccountTag).name === (value as IAccountTag).name;
				}}
				{...rest}
			/>
		</ComboboxSearchInputState.Provider>
	);
}

export function TagComboboxStatus(props: React.ComponentProps<typeof ComboboxStatus>) {
	const searchInputValue = useContext(ComboboxSearchInputState);
	const tagSearchQuery = useTagSearchQuery({
		query: searchInputValue,
		enabled: Boolean(searchInputValue),
	});
	return <_TagComboboxStatus tagSearchQuery={tagSearchQuery} {...props} />;
}

export function TagComboboxItem(props: { tag: IAccountTag } & React.ComponentProps<typeof ComboboxItem>) {
	const { tag, ...rest } = props;
	return (
		<ComboboxItem value={tag} {...rest}>
			<ComboboxItemRadioIndicator />
			<div className={css(bs.flex, bs.flexCol)}>
				<span className={css(bs.fontBold)}>{tag.name}</span>
				{tag.category?.name && <span className={css(bs.textSm, bs.textDarkGrayFontColor)}>{tag.category.name}</span>}
			</div>
		</ComboboxItem>
	);
}
